import React from "react";
import WebProfitSpinner from "../../../components/system/WebProfitSpinner";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CardContent,
  Chip,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddBusinessRoundedIcon from "@mui/icons-material/AddBusinessRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CallMadeRoundedIcon from "@mui/icons-material/CallMadeRounded";
import CallReceivedRoundedIcon from "@mui/icons-material/CallReceivedRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { erpApi } from "../../../api/erp";
import { productsApi } from "../../../api/products";
import { projectsApi } from "../../../api/projects";
import ContextualHelp from "../../../components/dashboard/ContextualHelp";
import ResponsiveOperationalTable from "../../../components/dashboard/ResponsiveOperationalTable";
import {
  ErpEmpty,
  ErpLoading,
  ErpPanel,
  ErpSectionTitle,
} from "../../../components/dashboard/erp/ErpUi";
import {
  ACCOUNT_TYPE_LABELS,
  CASH_REGISTER_KIND_HELP,
  CASH_REGISTER_KIND_LABELS,
  ERP_BRAND,
  cashRegisterNeedsSession,
  entityId,
  formatDateTime,
  formatMoney,
  uniqueRequestKey,
} from "../../../utils/erp";
import { responsiveActionsSx } from "../../../theme/webProfitResponsive";
import { hasProjectPermission } from "../../../utils/projectAccess";
import { useListScrollRestoration, usePersistentListValue } from "../../../utils/listViewState";

const initialRegister = {
  code: "",
  name: "",
  accountCode: "",
  kind: "cash",
  requiresSession: true,
  currency: "ARS",
  initialBalance: "0",
  branchName: "",
  branchCode: "",
  assignedUserIds: [],
  requireAssignment: true,
  blindClosing: true,
  maxCashOutWithoutApproval: "0",
  notes: "",
};
const REGISTER_PRESETS = [
  {
    key: "cash",
    label: "Efectivo",
    detail: "Plata física de la caja del local.",
    kind: "cash",
    name: "Efectivo",
  },
  {
    key: "bank",
    label: "Cuenta bancaria",
    detail: "Una cuenta del banco que use el negocio.",
    kind: "bank",
    name: "Cuenta bancaria",
  },
  {
    key: "digital_wallet",
    label: "Billetera digital",
    detail: "Mercado Pago u otra billetera donde recibís dinero.",
    kind: "digital_wallet",
    name: "Billetera digital",
  },
  {
    key: "other",
    label: "Otra caja o cuenta",
    detail: "Otro lugar real donde guardás o recibís plata.",
    kind: "other",
    name: "",
  },
];

const initialOpen = { openingAmount: "0", notes: "" };
const initialClose = { declaredClosingAmount: "", notes: "" };
const initialReview = {
  reviewStatus: "approved",
  reviewNotes: "",
  createAdjustment: false,
  adjustmentNotes: "",
};

const ITEM_KIND_LABELS = {
  product: "Producto",
  service: "Servicio",
  supply: "Insumo",
  expense: "Gasto",
  asset: "Activo",
  charge: "Cargo",
};

function itemKindOf(item = {}) {
  const value = String(item.itemKind || item.erpItemKind || item.tipo || "product")
    .trim()
    .toLowerCase();
  if (["servicio", "service"].includes(value)) return "service";
  if (["insumo", "supply"].includes(value)) return "supply";
  if (["gasto", "expense"].includes(value)) return "expense";
  if (["activo", "asset"].includes(value)) return "asset";
  if (["cargo", "charge"].includes(value)) return "charge";
  return "product";
}

function itemCapabilities(item = {}) {
  const kind = itemKindOf(item);
  const defaults = {
    product: { sellable: true, purchasable: true, stockTracked: true, expenseEnabled: false },
    service: { sellable: true, purchasable: false, stockTracked: false, expenseEnabled: false },
    supply: { sellable: false, purchasable: true, stockTracked: true, expenseEnabled: true },
    expense: { sellable: false, purchasable: true, stockTracked: false, expenseEnabled: true },
    asset: { sellable: false, purchasable: true, stockTracked: false, expenseEnabled: true },
    charge: { sellable: true, purchasable: true, stockTracked: false, expenseEnabled: true },
  }[kind] || {};
  return { ...defaults, ...(item.operational || item.capabilities || {}) };
}

function canSelectForDirection(item = {}, direction = "in") {
  if (item.activo === false || item.active === false) return false;
  const capabilities = itemCapabilities(item);
  return direction === "in"
    ? capabilities.sellable === true
    : capabilities.purchasable === true || capabilities.expenseEnabled === true;
}

function suggestedOperationAmount(item = {}, direction = "in") {
  if (direction === "in") {
    const price = item.precio ?? item.price;
    return Number(price || 0) > 0 ? String(Number(price)) : "";
  }
  const costing = item.costing || {};
  const method = costing.costingMethod || "last_cost";
  const methodValue = {
    last_cost: costing.lastCost,
    average_cost: costing.averageCost,
    standard_cost: costing.standardCost,
  }[method];
  const candidates = [
    methodValue,
    costing.lastCost,
    costing.averageCost,
    costing.standardCost,
    item.precio,
    item.price,
  ];
  const value = candidates.find((candidate) => Number(candidate || 0) > 0);
  return value === undefined ? "" : String(Number(value));
}

function itemTaxCode(item = {}) {
  return String(item.tax?.profileCode || item.taxProfileCode || "").trim().toUpperCase();
}

function itemDuration(item = {}) {
  return Number(item.durationMinutes ?? item.duracionMinutos ?? 0);
}

function itemOptionLabel(item = {}) {
  const code = String(item.codigo || item.code || "").trim();
  const name = String(item.nombre || item.name || "Ítem").trim();
  return code ? `${name} · ${code}` : name;
}

const filterOperationItems = createFilterOptions({
  stringify: (item) =>
    [
      item.nombre || item.name,
      item.codigo || item.code,
      item.categoria || item.category,
      item.unidad || item.unit,
      ITEM_KIND_LABELS[itemKindOf(item)],
    ]
      .filter(Boolean)
      .join(" "),
});

function newOperationForm() {
  return {
    direction: "in",
    itemId: "",
    quantity: "1",
    amount: "",
    description: "",
    counterAccountCode: "OTHER_INCOME",
    reference: "",
    notes: "",
    idempotencyKey: uniqueRequestKey("cash-ui"),
  };
}

function memberUser(member = {}) {
  return member.userId || member.user || {};
}

function registerToForm(register = {}) {
  return {
    ...initialRegister,
    code: register.code || "",
    name: register.name || "",
    accountCode: register.accountCode || "",
    kind: register.kind || "cash",
    requiresSession: cashRegisterNeedsSession(register),
    currency: register.currency || "ARS",
    initialBalance: "0",
    branchName: register.branchName || "",
    branchCode: register.branchCode || "",
    assignedUserIds: Array.isArray(register.assignedUserIds)
      ? register.assignedUserIds.map((user) => entityId(user))
      : [],
    requireAssignment: register.requireAssignment === true,
    blindClosing: register.blindClosing !== false,
    maxCashOutWithoutApproval: String(register.maxCashOutWithoutApproval || 0),
    notes: register.notes || "",
  };
}

export default function ProjectErpCashPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { access, bootstrap, refreshBootstrap } = useOutletContext();
  const [registers, setRegisters] = React.useState([]);
  const [sessions, setSessions] = React.useState([]);
  const [cashMovements, setCashMovements] = React.useState([]);
  const [members, setMembers] = React.useState([]);
  const [blindClosing, setBlindClosing] = React.useState(access?.blindCashClosing === true);
  const [movementsTotal, setMovementsTotal] = React.useState(0);
  const [page, setPage] = usePersistentListValue(`erp-cash:${projectId}:page`, 0);
  const [rowsPerPage, setRowsPerPage] = usePersistentListValue(`erp-cash:${projectId}:page-size`, 15);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [registerDialog, setRegisterDialog] = React.useState(false);
  const [registerEditing, setRegisterEditing] = React.useState(null);
  const [registerForm, setRegisterForm] = React.useState(initialRegister);
  const [registerPreset, setRegisterPreset] = React.useState("");
  const [showInitialBalance, setShowInitialBalance] = React.useState(false);
  const [showAdvancedRegisterOptions, setShowAdvancedRegisterOptions] = React.useState(false);
  const [openTarget, setOpenTarget] = React.useState(null);
  const [openForm, setOpenForm] = React.useState(initialOpen);
  const [closeTarget, setCloseTarget] = React.useState(null);
  const [closeForm, setCloseForm] = React.useState(initialClose);
  const [operationTarget, setOperationTarget] = React.useState(null);
  const [operationForm, setOperationForm] = React.useState(newOperationForm);
  const [operationItems, setOperationItems] = React.useState([]);
  const [operationItemsLoading, setOperationItemsLoading] = React.useState(false);
  const [operationItemsError, setOperationItemsError] = React.useState("");
  const [reviewTarget, setReviewTarget] = React.useState(null);
  const [reviewForm, setReviewForm] = React.useState(initialReview);

  const canReadRegisters = hasProjectPermission(access, "cash.registers.read");
  const canManageRegisters = hasProjectPermission(access, "cash.registers.manage");
  const canReadSessions = hasProjectPermission(access, "cash.sessions.read");
  const canReadMovements = hasProjectPermission(access, "cash.movements.read");
  const canOpen = hasProjectPermission(access, "cash.sessions.open");
  const canClose =
    hasProjectPermission(access, "cash.sessions.close_own") ||
    hasProjectPermission(access, "cash.sessions.close_any");
  const canReview = hasProjectPermission(access, "cash.sessions.review");
  const canCashIn =
    hasProjectPermission(access, "cash.operations.create") ||
    hasProjectPermission(access, "cash.operations.in");
  const canCashOut =
    hasProjectPermission(access, "cash.operations.create") ||
    hasProjectPermission(access, "cash.operations.out");
  const canOperate = canCashIn || canCashOut;
  const canCreatePurchases = hasProjectPermission(access, "erp.purchases.create");
  const canCreateExpenses = hasProjectPermission(access, "erp.expenses.create");
  const canRegisterItemIncome =
    canCashIn &&
    hasProjectPermission(access, "erp.sales.create") &&
    hasProjectPermission(access, "erp.collections.create");
  const canRegisterItemExpense =
    canCashOut &&
    hasProjectPermission(access, "erp.payments.create") &&
    (canCreatePurchases || canCreateExpenses);
  const canReadItemCosts = hasProjectPermission(access, "items.costs.read");
  const canReadExpected = hasProjectPermission(access, "cash.expected.read");

  const openNewRegisterDialog = React.useCallback(() => {
    setRegisterEditing(null);
    setRegisterForm(initialRegister);
    setRegisterPreset("");
    setShowInitialBalance(false);
    setShowAdvancedRegisterOptions(false);
    setRegisterDialog(true);
  }, []);

  const selectRegisterPreset = React.useCallback((preset) => {
    setRegisterPreset(preset.key);
    setRegisterForm((current) => ({
      ...current,
      name: preset.name,
      kind: preset.kind,
      requiresSession: preset.kind === "cash",
    }));
  }, []);

  const closeRegisterDialog = React.useCallback(() => {
    setRegisterDialog(false);
    setRegisterEditing(null);
    setRegisterForm(initialRegister);
    setRegisterPreset("");
    setShowInitialBalance(false);
    setShowAdvancedRegisterOptions(false);
  }, []);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [registerData, sessionData, movementData] = await Promise.all([
        canReadRegisters
          ? erpApi.listCashRegisters(projectId)
          : Promise.resolve({ registers: [], blindClosing: true }),
        canReadSessions
          ? erpApi.listCashSessions(projectId, { page: 1, pageSize: 30 })
          : Promise.resolve({ cashSessions: [], blindClosing: true }),
        canReadMovements
          ? erpApi.listCashMovements(projectId, {
              page: page + 1,
              pageSize: rowsPerPage,
            })
          : Promise.resolve({ cashMovements: [], total: 0 }),
      ]);
      setRegisters(registerData.registers || []);
      setSessions(sessionData.cashSessions || []);
      setCashMovements(movementData.cashMovements || []);
      setMovementsTotal(Number(movementData.total || 0));
      setBlindClosing(
        registerData.blindClosing === true ||
          sessionData.blindClosing === true ||
          access?.blindCashClosing === true,
      );
    } catch (err) {
      setError(err.message || "No se pudo cargar el módulo de caja");
    } finally {
      setLoading(false);
    }
  }, [
    projectId,
    page,
    rowsPerPage,
    canReadRegisters,
    canReadSessions,
    canReadMovements,
    access?.blindCashClosing,
  ]);

  React.useEffect(() => {
    load();
  }, [load]);

  useListScrollRestoration(`erp-cash:${projectId}`, !loading);

  React.useEffect(() => {
    if (searchParams.get("crear") !== "1" || !canManageRegisters) return;
    openNewRegisterDialog();
    const next = new URLSearchParams(searchParams);
    next.delete("crear");
    setSearchParams(next, { replace: true });
  }, [canManageRegisters, openNewRegisterDialog, searchParams, setSearchParams]);

  React.useEffect(() => {
    if (!canManageRegisters) return;
    projectsApi
      .listMembers(projectId)
      .then((data) =>
        setMembers((data.members || []).filter((member) => member.status !== "suspended")),
      )
      .catch(() => setMembers([]));
  }, [projectId, canManageRegisters]);

  React.useEffect(() => {
    if (!canOperate) {
      setOperationItems([]);
      return undefined;
    }

    let active = true;
    setOperationItemsLoading(true);
    setOperationItemsError("");

    productsApi
      .list(projectId, { active: true })
      .then((data) => {
        if (!active) return;
        const items = (data.products || [])
          .filter((item) => item.activo !== false && item.active !== false)
          .sort((left, right) => {
            const kindOrder = { product: 0, service: 1, charge: 2, supply: 3, expense: 4, asset: 5 };
            const byKind = (kindOrder[itemKindOf(left)] ?? 9) - (kindOrder[itemKindOf(right)] ?? 9);
            if (byKind !== 0) return byKind;
            return itemOptionLabel(left).localeCompare(itemOptionLabel(right), "es");
          });
        setOperationItems(items);
      })
      .catch((err) => {
        if (!active) return;
        setOperationItems([]);
        setOperationItemsError(err.message || "No se pudieron cargar los ítems.");
      })
      .finally(() => {
        if (active) setOperationItemsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [projectId, canOperate]);

  const sync = async () => {
    await Promise.all([load(), refreshBootstrap()]);
  };

  const createRegister = async () => {
    if (!registerEditing && !registerPreset) {
      setError("Elegí dónde querés registrar la plata antes de continuar.");
      return;
    }
    if (!registerForm.name.trim()) {
      setError("Poné un nombre que reconozcas, por ejemplo Efectivo o Mercado Pago.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const payload = {
        ...registerForm,
        code: registerForm.code ? registerForm.code.toUpperCase() : undefined,
        branchCode: registerForm.branchCode.toUpperCase(),
        accountCode: registerForm.accountCode ? registerForm.accountCode.toUpperCase() : undefined,
        maxCashOutWithoutApproval: Number(registerForm.maxCashOutWithoutApproval || 0),
      };
      if (registerEditing) delete payload.initialBalance;
      else payload.initialBalance = Number(registerForm.initialBalance || 0);
      let savedRegister = registerEditing;
      if (registerEditing) {
        const result = await erpApi.updateCashRegister(projectId, entityId(registerEditing), payload);
        savedRegister = result?.register || registerEditing;
      } else {
        const result = await erpApi.createCashRegister(projectId, payload);
        savedRegister = result?.register || payload;
      }
      closeRegisterDialog();
      setMessage(
        registerEditing
          ? "Caja o cuenta actualizada."
          : cashRegisterNeedsSession(savedRegister)
            ? `Listo: ${savedRegister?.name || "la caja"} quedó configurada. Para usarla, abrí la caja desde esta pantalla.`
            : `Listo: ${savedRegister?.name || "la cuenta"} quedó lista para registrar movimientos.`,
      );
      await sync();
    } catch (err) {
      setError(err.message || "No se pudo crear la caja");
    } finally {
      setSaving(false);
    }
  };

  const openRegister = async () => {
    try {
      setSaving(true);
      setError("");
      setMessage("");
      await erpApi.openCashRegister(projectId, entityId(openTarget?.register), {
        openingAmount: Number(openForm.openingAmount || 0),
        notes: openForm.notes,
      });
      setOpenTarget(null);
      setOpenForm(initialOpen);
      setMessage("Caja abierta correctamente.");
      await sync();
    } catch (err) {
      setError(err.message || "No se pudo abrir la caja");
    } finally {
      setSaving(false);
    }
  };

  const closeRegister = async () => {
    if (closeForm.declaredClosingAmount === "") {
      setError("Contá el efectivo e ingresá el saldo declarado.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const result = await erpApi.closeCashSession(
        projectId,
        entityId(closeTarget?.openSession),
        {
          declaredClosingAmount: Number(closeForm.declaredClosingAmount || 0),
          notes: closeForm.notes,
        },
      );
      const difference = result?.totals?.difference;
      setCloseTarget(null);
      setCloseForm(initialClose);
      setMessage(
        difference === undefined
          ? "Caja cerrada y enviada a revisión."
          : `Caja cerrada. Diferencia registrada: ${formatMoney(
              difference,
              closeTarget?.register?.currency,
            )}.`,
      );
      await sync();
    } catch (err) {
      setError(err.message || "No se pudo cerrar la caja");
    } finally {
      setSaving(false);
    }
  };

  const recordOperation = async () => {
    const selectedItem = operationItems.find(
      (item) => entityId(item) === String(operationForm.itemId || ""),
    );
    const quantity = Number(operationForm.quantity || 1);
    if (Number(operationForm.amount || 0) <= 0 || !operationForm.description.trim()) {
      setError("Completá una descripción y un importe mayor a cero.");
      return;
    }
    if (selectedItem && quantity <= 0) {
      setError("La cantidad debe ser mayor a cero.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const result = await erpApi.recordCashOperation(projectId, {
        registerId: entityId(operationTarget?.register),
        direction: operationForm.direction,
        itemId: selectedItem ? entityId(selectedItem) : undefined,
        quantity: selectedItem ? quantity : undefined,
        unitAmount: selectedItem ? Number(operationForm.amount) : undefined,
        amount: Number(operationForm.amount),
        description: operationForm.description,
        counterAccountCode: selectedItem ? undefined : operationForm.counterAccountCode,
        reference: operationForm.reference,
        notes: operationForm.notes,
        idempotencyKey: operationForm.idempotencyKey,
        metadata: selectedItem
          ? {
              itemId: entityId(selectedItem),
              itemSnapshot: {
                code: selectedItem.codigo || selectedItem.code || "",
                name: selectedItem.nombre || selectedItem.name || "",
                kind: itemKindOf(selectedItem),
                category: selectedItem.categoria || selectedItem.category || "",
                unit: selectedItem.unidad || selectedItem.unit || "",
                taxProfileCode: itemTaxCode(selectedItem),
              },
            }
          : {},
      });
      const movementTotal = Number(result?.movement?.total || 0);
      setOperationTarget(null);
      setOperationForm(newOperationForm());
      setMessage(
        selectedItem
          ? `${operationForm.direction === "in" ? "Venta cobrada" : "Compra o gasto pagado"} y registrado por ${formatMoney(
              movementTotal || Number(operationForm.amount) * quantity,
              operationTarget?.register?.currency,
            )}.`
          : "Operación manual de caja registrada.",
      );
      await sync();
    } catch (err) {
      setError(err.message || "No se pudo registrar la operación de caja");
    } finally {
      setSaving(false);
    }
  };

  const reviewSession = async () => {
    try {
      setSaving(true);
      setError("");
      setMessage("");
      await erpApi.reviewCashSession(projectId, entityId(reviewTarget), reviewForm);
      setReviewTarget(null);
      setReviewForm(initialReview);
      setMessage("Cierre revisado y auditado.");
      await sync();
    } catch (err) {
      setError(err.message || "No se pudo revisar el cierre");
    } finally {
      setSaving(false);
    }
  };

  const incomeAccounts = (bootstrap.accounts || []).filter(
    (account) => account.active && ["income", "liability", "equity"].includes(account.type),
  );
  const expenseAccounts = (bootstrap.accounts || []).filter(
    (account) => account.active && ["expense", "asset", "liability"].includes(account.type),
  );
  const operationAccounts =
    operationForm.direction === "in" ? incomeAccounts : expenseAccounts;
  const canUseItemSelector =
    operationForm.direction === "in" ? canRegisterItemIncome : canRegisterItemExpense;
  const selectableOperationItems = operationItems.filter((item) => {
    if (!canSelectForDirection(item, operationForm.direction)) return false;
    if (operationForm.direction === "in") return canRegisterItemIncome;
    const capabilities = itemCapabilities(item);
    const becomesPurchase = capabilities.stockTracked && capabilities.purchasable;
    if (becomesPurchase) return canCreatePurchases;
    if (capabilities.expenseEnabled) return canCreateExpenses;
    return canCreatePurchases;
  });
  const selectedOperationItem = selectableOperationItems.find(
    (item) => entityId(item) === String(operationForm.itemId || ""),
  ) || null;
  const selectedTaxProfile = selectedOperationItem
    ? (bootstrap.taxProfiles || []).find(
        (profile) => String(profile.code || "").toUpperCase() === itemTaxCode(selectedOperationItem),
      ) || null
    : null;
  const selectedCapabilities = selectedOperationItem
    ? itemCapabilities(selectedOperationItem)
    : null;
  const selectedAccountCode = selectedOperationItem
    ? operationForm.direction === "in"
      ? selectedOperationItem.accounting?.salesAccountCode || "SALES"
      : selectedCapabilities?.stockTracked
        ? selectedOperationItem.accounting?.inventoryAccountCode || "INVENTORY"
        : selectedOperationItem.accounting?.expenseAccountCode ||
          selectedOperationItem.accounting?.purchaseAccountCode ||
          "OTHER_EXPENSE"
    : operationForm.counterAccountCode;
  const selectedAccount = (bootstrap.accounts || []).find(
    (account) => account.code === selectedAccountCode,
  );
  const quantityValue = Math.max(1, Number(operationForm.quantity || 1));
  const unitAmountValue = Math.max(0, Number(operationForm.amount || 0));
  const taxRate = Number(selectedTaxProfile?.rate ?? selectedOperationItem?.tax?.rate ?? 0);
  const taxMode = String(
    selectedTaxProfile?.calculationMode || selectedOperationItem?.tax?.calculationMode || "percentage",
  ).toLowerCase();
  const taxIncluded = Boolean(
    selectedTaxProfile?.includedInPrice ?? selectedOperationItem?.tax?.includedInPrice,
  );
  const baseOperationAmount = unitAmountValue * quantityValue;
  const estimatedTax = taxRate <= 0
    ? 0
    : taxMode === "fixed"
      ? taxRate
      : taxIncluded
        ? baseOperationAmount - baseOperationAmount / (1 + taxRate / 100)
        : baseOperationAmount * (taxRate / 100);
  const estimatedOperationTotal = taxIncluded
    ? baseOperationAmount
    : baseOperationAmount + estimatedTax;

  if (loading && !registers.length) {
    return <ErpLoading label="Cargando cajas y arqueos..." />;
  }

  return (
    <Stack spacing={2.5}>
      {error ? (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}
      {message ? (
        <Alert severity="success" onClose={() => setMessage("")}>
          {message}
        </Alert>
      ) : null}

      <ErpSectionTitle
        title="Cajas y cuentas financieras"
        description="Lugares reales donde está o se mueve la plata: efectivo, bancos, billeteras y otras cuentas financieras."
        action={
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="outlined"
              startIcon={loading ? <WebProfitSpinner size={16} decorative /> : <RefreshRoundedIcon />}
              onClick={load}
              disabled={loading}
              sx={{ textTransform: "none", fontWeight: 850 }}
            >
              {loading ? "Actualizando…" : "Actualizar"}
            </Button>
            {canManageRegisters ? (
              <Button
                variant="contained"
                startIcon={<AddBusinessRoundedIcon />}
                onClick={openNewRegisterDialog}
                sx={{ textTransform: "none", fontWeight: 850 }}
              >
                Nueva caja o cuenta financiera
              </Button>
            ) : null}
          </Stack>
        }
      />

      {blindClosing ? (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, px: .25 }}>
          <Chip size="small" label="Cierre ciego activo" variant="outlined" />
          <ContextualHelp
            compact
            title="Cierre ciego"
            text="El operador declara el efectivo contado sin ver el saldo esperado. La diferencia se muestra después del cierre y queda auditada."
          />
        </Box>
      ) : null}

      {!registers.length ? (
        <ErpPanel>
          <ErpEmpty
            title="Creá una caja o cuenta financiera"
            description={
              canManageRegisters
                ? "Ejemplos: Efectivo, Banco Nación, Mercado Pago, Caja dólares o Dinero del dueño. No se crean cajas ficticias por defecto."
                : "No tenés cajas o cuentas asignadas, o todavía no se configuró ninguna."
            }
            action={
              canManageRegisters ? (
                <Button
                  variant="contained"
                  startIcon={<AddBusinessRoundedIcon />}
                  onClick={openNewRegisterDialog}
                >
                  Crear la primera
                </Button>
              ) : null
            }
          />
        </ErpPanel>
      ) : (
        <Grid container spacing={2}>
          {registers.map((item) => {
            const register = item.register;
            const isOpen = Boolean(item.openSession);
            const requiresSession = cashRegisterNeedsSession(register);
            const usable = !requiresSession || isOpen;
            const totalsHidden = item.totals?.hidden === true || !canReadExpected;
            return (
              <Grid item xs={12} md={6} xl={4} key={entityId(register)}>
                <ErpPanel sx={{ height: "100%" }}>
                  <CardContent sx={{ p: { xs: 1.6, sm: 2.5 } }}>
                    <Stack direction="row" justifyContent="space-between" spacing={1.25} alignItems="flex-start">
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography variant="h6" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>
                            {register.name}
                          </Typography>
                          <Chip
                            size="small"
                            color={usable ? "success" : "default"}
                            label={requiresSession ? (isOpen ? "Abierta" : "Cerrada") : "Lista para usar"}
                            sx={{ fontWeight: 850 }}
                          />
                          {requiresSession && register.blindClosing ? (
                            <Chip size="small" label="Cierre ciego" variant="outlined" />
                          ) : null}
                        </Stack>
                        <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.5 }}>
                          {CASH_REGISTER_KIND_LABELS[register.kind] || "Caja o cuenta"} · {register.currency}
                        </Typography>
                        {register.branchName ? (
                          <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
                            {register.branchName}
                          </Typography>
                        ) : null}
                      </Box>
                      <Box
                        sx={{
                          width: 46,
                          height: 46,
                          borderRadius: 3,
                          display: "grid",
                          placeItems: "center",
                          bgcolor: isOpen ? "rgba(39,174,96,0.1)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.06)",
                          color: isOpen ? "success.main" : ERP_BRAND.muted,
                        }}
                      >
                        <PointOfSaleRoundedIcon />
                      </Box>
                    </Stack>

                    {isOpen ? (
                      <Box sx={{ mt: 2.2, p: 2, bgcolor: ERP_BRAND.soft, borderRadius: 2.5 }}>
                        <Grid container spacing={1.5}>
                          <Grid item xs={totalsHidden ? 6 : 4}>
                            <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
                              Apertura
                            </Typography>
                            <Typography sx={{ fontWeight: 900 }}>
                              {formatMoney(item.openSession.openingAmount, register.currency)}
                            </Typography>
                          </Grid>
                          {totalsHidden ? (
                            <Grid item xs={6}>
                              <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
                                Arqueo
                              </Typography>
                              <Typography sx={{ fontWeight: 900 }}>Saldo oculto</Typography>
                            </Grid>
                          ) : (
                            <>
                              <Grid item xs={4}>
                                <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
                                  Ingresos
                                </Typography>
                                <Typography sx={{ fontWeight: 900, color: "success.main" }}>
                                  {formatMoney(item.totals?.incoming, register.currency)}
                                </Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
                                  Esperado
                                </Typography>
                                <Typography sx={{ fontWeight: 950 }}>
                                  {formatMoney(item.totals?.expected, register.currency)}
                                </Typography>
                              </Grid>
                            </>
                          )}
                        </Grid>
                        <Typography variant="caption" sx={{ color: ERP_BRAND.muted, display: "block", mt: 1.2 }}>
                          Abierta {formatDateTime(item.openSession.openedAt)}
                          {!totalsHidden
                            ? ` · egresos ${formatMoney(item.totals?.outgoing, register.currency)}`
                            : " · el total se calcula al cerrar"}
                        </Typography>
                      </Box>
                    ) : null}

                    {!requiresSession ? (
                      <Box sx={{ mt: 2.2, p: 2, bgcolor: ERP_BRAND.soft, borderRadius: 2.5 }}>
                        <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>Saldo registrado</Typography>
                        {item.balance?.hidden ? (
                          <>
                            <Typography sx={{ fontWeight: 900, color: ERP_BRAND.dark }}>Saldo oculto por permisos</Typography>
                            <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
                              Podés operar esta cuenta, pero no ver los totales financieros del negocio.
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Typography variant="h5" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>
                              {formatMoney(item.balance?.balance, register.currency)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
                              {item.balance?.operationCount || 0} movimientos · no necesita apertura ni cierre
                            </Typography>
                          </>
                        )}
                      </Box>
                    ) : null}

                    <Stack sx={{ ...responsiveActionsSx, mt: 2.2 }}>
                      {canManageRegisters ? (
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setRegisterEditing(register);
                            setRegisterForm(registerToForm(register));
                            setRegisterPreset("");
                            setShowInitialBalance(false);
                            setShowAdvancedRegisterOptions(false);
                            setRegisterDialog(true);
                          }}
                          sx={{ textTransform: "none", fontWeight: 850, order: { xs: 5, sm: 0 } }}
                        >
                          Configurar
                        </Button>
                      ) : null}
                      {requiresSession && !isOpen && canOpen ? (
                        <Button
                          variant="contained"
                          startIcon={<LockOpenRoundedIcon />}
                          onClick={() => {
                            setOpenTarget(item);
                            setOpenForm(initialOpen);
                          }}
                          sx={{ textTransform: "none", fontWeight: 850 }}
                        >
                          Abrir caja
                        </Button>
                      ) : null}
                      {usable && canOperate ? (
                        <Button
                          variant="contained"
                          startIcon={<AddRoundedIcon />}
                          onClick={() => navigate(`/dashboard/proyectos/${projectId}/sistema/movimientos?nuevo=1`)}
                          sx={{ textTransform: "none", fontWeight: 850 }}
                        >
                          Registrar algo
                        </Button>
                      ) : null}
                      {requiresSession && isOpen && canClose ? (
                        <Button
                          variant="outlined"
                          color="warning"
                          startIcon={<LockRoundedIcon />}
                          onClick={() => {
                            setCloseTarget(item);
                            setCloseForm(initialClose);
                          }}
                          sx={{ textTransform: "none", fontWeight: 850 }}
                        >
                          Cerrar
                        </Button>
                      ) : null}
                    </Stack>
                  </CardContent>
                </ErpPanel>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Grid container spacing={2.5}>
        {canReadMovements ? (
          <Grid item xs={12} xl={7.5}>
            <ErpPanel>
              <Box sx={{ p: 2.5, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
                <Typography variant="h5" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>
                  Movimientos de caja
                </Typography>
                <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.35 }}>
                  Cobros, pagos y movimientos directos que podés consultar.
                </Typography>
              </Box>
              {!cashMovements.length ? (
                <ErpEmpty
                  title="Sin movimientos de caja"
                  description="Los cobros, pagos e ingresos directos aparecerán aquí."
                />
              ) : (
                <>
                  <TableContainer>
                    <ResponsiveOperationalTable mobileLabel="Movimientos de caja" collapseAfter={3}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Movimiento</TableCell>
                          <TableCell>Caja</TableCell>
                          <TableCell>Fecha</TableCell>
                          <TableCell align="right">Importe</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cashMovements.map((movement) => (
                          <TableRow key={entityId(movement)} hover>
                            <TableCell>
                              <Typography sx={{ fontWeight: 850 }}>
                                {movement.cashMovementNumber}
                              </Typography>
                              <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
                                {movement.description}
                              </Typography>
                            </TableCell>
                            <TableCell>{movement.registerId?.name || "—"}</TableCell>
                            <TableCell>{formatDateTime(movement.occurredAt)}</TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 950,
                                color:
                                  movement.direction === "in"
                                    ? "success.main"
                                    : movement.direction === "out"
                                      ? "error.main"
                                      : ERP_BRAND.dark,
                              }}
                            >
                              {movement.direction === "in"
                                ? "+"
                                : movement.direction === "out"
                                  ? "-"
                                  : ""}
                              {formatMoney(movement.amount, movement.currency)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </ResponsiveOperationalTable>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={movementsTotal}
                    page={page}
                    onPageChange={(_event, nextPage) => setPage(nextPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(event) => {
                      setRowsPerPage(Number(event.target.value));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[10, 15, 25, 50]}
                    labelRowsPerPage="Filas por página"
                  />
                </>
              )}
            </ErpPanel>
          </Grid>
        ) : null}

        {canReadSessions ? (
          <Grid item xs={12} xl={canReadMovements ? 4.5 : 12}>
            <ErpPanel>
              <Box sx={{ p: 2.5, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
                <Typography variant="h5" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>
                  Últimas sesiones
                </Typography>
                <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.35 }}>
                  Aperturas, cierres y diferencias recientes.
                </Typography>
              </Box>
              {!sessions.length ? (
                <ErpEmpty
                  title="Sin sesiones"
                  description="Cuando abras una caja se generará la primera sesión."
                />
              ) : (
                <Stack spacing={0} sx={{ px: 2.5 }}>
                  {sessions.slice(0, 15).map((session, index) => (
                    <Box
                      key={entityId(session)}
                      sx={{ py: 1.8, borderTop: index ? `1px solid ${ERP_BRAND.border}` : "none" }}
                    >
                      <Stack direction="row" justifyContent="space-between" spacing={2}>
                        <Box>
                          <Typography sx={{ fontWeight: 900 }}>
                            {session.registerId?.name || "Caja"}
                          </Typography>
                          <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
                            {session.sessionNumber} · {formatDateTime(session.openedAt)}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          color={session.status === "open" ? "success" : "default"}
                          label={session.status === "open" ? "Abierta" : "Cerrada"}
                        />
                      </Stack>
                      {session.status === "closed" ? (
                        <Stack spacing={0.8} sx={{ mt: 1 }}>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>
                              Declarado
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 900 }}>
                              {formatMoney(session.declaredClosingAmount, session.currency)}
                            </Typography>
                          </Stack>
                          {session.difference !== undefined && session.difference !== null ? (
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>
                                Diferencia
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 900,
                                  color:
                                    Number(session.difference) === 0
                                      ? "success.main"
                                      : "warning.main",
                                }}
                              >
                                {formatMoney(session.difference, session.currency)}
                              </Typography>
                            </Stack>
                          ) : null}
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Chip
                              size="small"
                              label={
                                session.reviewStatus === "approved"
                                  ? "Aprobado"
                                  : session.reviewStatus === "observed"
                                    ? "Observado"
                                    : session.reviewStatus === "pending"
                                      ? "Pendiente de revisión"
                                      : "Sin revisión"
                              }
                              color={session.reviewStatus === "approved" ? "success" : "default"}
                            />
                            {canReview && session.reviewStatus !== "approved" ? (
                              <Button
                                size="small"
                                startIcon={<FactCheckRoundedIcon />}
                                onClick={() => {
                                  setReviewTarget(session);
                                  setReviewForm(initialReview);
                                }}
                                sx={{ textTransform: "none", fontWeight: 850 }}
                              >
                                Revisar
                              </Button>
                            ) : null}
                          </Stack>
                        </Stack>
                      ) : null}
                    </Box>
                  ))}
                </Stack>
              )}
            </ErpPanel>
          </Grid>
        ) : null}
      </Grid>

      <Dialog
        open={registerDialog}
        onClose={saving ? undefined : closeRegisterDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: 950 }}>
          {registerEditing ? "Configurar caja o cuenta" : "¿Dónde querés que WebProfit registre la plata?"}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2.2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {registerEditing
                ? "Actualizá el nombre o la configuración de este lugar."
                : "Elegí el lugar real donde queda el dinero cuando cobrás una venta."}
            </Typography>

            {!registerEditing ? (
              <Grid container spacing={1.2}>
                {REGISTER_PRESETS.map((preset) => {
                  const selected = registerPreset === preset.key;
                  return (
                    <Grid item xs={12} sm={6} key={preset.key}>
                      <Button
                        fullWidth
                        variant={selected ? "contained" : "outlined"}
                        onClick={() => selectRegisterPreset(preset)}
                        sx={{
                          minHeight: 78,
                          justifyContent: "flex-start",
                          textAlign: "left",
                          textTransform: "none",
                          borderRadius: 2.5,
                          px: 1.8,
                          boxShadow: "none",
                        }}
                      >
                        <Stack spacing={0.25} alignItems="flex-start">
                          <Typography component="span" sx={{ fontWeight: 950 }}>
                            {preset.label}
                          </Typography>
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ color: selected ? "inherit" : "text.secondary", lineHeight: 1.25 }}
                          >
                            {preset.detail}
                          </Typography>
                        </Stack>
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>
            ) : null}

            {(registerEditing || registerPreset) ? (
              <Stack spacing={1.6}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={registerEditing || registerPreset === "other" ? 7 : 12}>
                    <TextField
                      label="Nombre que reconocés"
                      required
                      value={registerForm.name}
                      onChange={(event) => setRegisterForm((current) => ({ ...current, name: event.target.value }))}
                      placeholder="Ej.: Efectivo, Banco Nación, Mercado Pago"
                      autoFocus
                      fullWidth
                    />
                  </Grid>
                  {(registerEditing || registerPreset === "other") ? (
                    <Grid item xs={12} md={5}>
                      <TextField
                        select
                        label="Qué tipo de lugar es"
                        value={registerForm.kind}
                        onChange={(event) => {
                          const kind = event.target.value;
                          setRegisterForm((current) => ({
                            ...current,
                            kind,
                            requiresSession: kind === "cash",
                          }));
                        }}
                        helperText={CASH_REGISTER_KIND_HELP[registerForm.kind]}
                        fullWidth
                      >
                        {Object.entries(CASH_REGISTER_KIND_LABELS).map(([value, label]) => (
                          <MenuItem key={value} value={value}>{label}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  ) : null}
                </Grid>

                {!registerEditing ? (
                  <Box>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setShowInitialBalance((current) => !current)}
                      sx={{ textTransform: "none", fontWeight: 850, px: 0 }}
                    >
                      {showInitialBalance ? "Ocultar saldo inicial" : "Ya había plata acá al empezar"}
                    </Button>
                    {showInitialBalance ? (
                      <Box sx={{ mt: 1 }}>
                        <TextField
                          type="number"
                          label="Cuánto había al empezar"
                          value={registerForm.initialBalance}
                          onChange={(event) => setRegisterForm((current) => ({ ...current, initialBalance: event.target.value }))}
                          inputProps={{ min: 0, step: "any" }}
                          helperText="Si no había plata o preferís cargarla después, dejalo en 0."
                          fullWidth
                        />
                      </Box>
                    ) : null}
                  </Box>
                ) : null}

                <Box>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setShowAdvancedRegisterOptions((current) => !current)}
                    sx={{ textTransform: "none", fontWeight: 850, px: 0 }}
                  >
                    {showAdvancedRegisterOptions ? "Ocultar opciones avanzadas" : "Opciones avanzadas"}
                  </Button>

                  {showAdvancedRegisterOptions ? (
                    <Stack spacing={1.6} sx={{ mt: 1.2 }}>
                      <Alert severity="info">
                        Estas opciones son administrativas. No necesitás cambiarlas para empezar a vender.
                      </Alert>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Moneda"
                            value={registerForm.currency}
                            onChange={(event) => setRegisterForm((current) => ({ ...current, currency: event.target.value.toUpperCase() }))}
                            placeholder="ARS"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Código de sucursal"
                            value={registerForm.branchCode}
                            onChange={(event) =>
                              setRegisterForm((current) => ({
                                ...current,
                                branchCode: event.target.value.toUpperCase(),
                              }))
                            }
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Sucursal o ubicación"
                            value={registerForm.branchName}
                            onChange={(event) =>
                              setRegisterForm((current) => ({
                                ...current,
                                branchName: event.target.value,
                              }))
                            }
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            type="number"
                            label="Máximo egreso sin aprobación"
                            value={registerForm.maxCashOutWithoutApproval}
                            onChange={(event) =>
                              setRegisterForm((current) => ({
                                ...current,
                                maxCashOutWithoutApproval: event.target.value,
                              }))
                            }
                            inputProps={{ min: 0, step: "any" }}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            select
                            SelectProps={{ multiple: true }}
                            label="Personas asignadas"
                            value={registerForm.assignedUserIds}
                            onChange={(event) =>
                              setRegisterForm((current) => ({
                                ...current,
                                assignedUserIds:
                                  typeof event.target.value === "string"
                                    ? event.target.value.split(",")
                                    : event.target.value,
                              }))
                            }
                            helperText="Usalo si sólo algunas personas deben operar este lugar."
                            fullWidth
                          >
                            {members.map((member) => {
                              const person = memberUser(member);
                              return (
                                <MenuItem key={entityId(member)} value={entityId(person)}>
                                  {person.name || person.email} · {member.role}
                                </MenuItem>
                              );
                            })}
                          </TextField>
                        </Grid>
                      </Grid>

                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={registerForm.requireAssignment}
                              onChange={(event) =>
                                setRegisterForm((current) => ({
                                  ...current,
                                  requireAssignment: event.target.checked,
                                }))
                              }
                            />
                          }
                          label="Exigir asignación para operar"
                        />
                        {registerForm.requiresSession ? (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={registerForm.blindClosing}
                                onChange={(event) => setRegisterForm((current) => ({ ...current, blindClosing: event.target.checked }))}
                              />
                            }
                            label="Ocultar saldo esperado durante el cierre"
                          />
                        ) : null}
                      </Stack>

                      <TextField
                        label="Notas"
                        multiline
                        minRows={2}
                        value={registerForm.notes}
                        onChange={(event) =>
                          setRegisterForm((current) => ({ ...current, notes: event.target.value }))
                        }
                        fullWidth
                      />
                    </Stack>
                  ) : null}
                </Box>
              </Stack>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={closeRegisterDialog} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={createRegister}
            disabled={saving || (!registerEditing && (!registerPreset || !registerForm.name.trim()))}
            startIcon={saving ? <WebProfitSpinner size={16} /> : undefined}
          >
            {saving ? "Guardando..." : registerEditing ? "Guardar cambios" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(openTarget)}
        onClose={saving ? undefined : () => setOpenTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 950 }}>Abrir {openTarget?.register?.name}</DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">
              Esto abre el turno de trabajo de la caja física. No es el saldo inicial contable del negocio: ingresá únicamente el efectivo contado que recibe la persona al comenzar este turno.
            </Alert>
            <TextField
              type="number"
              label="Efectivo contado al abrir el turno"
              value={openForm.openingAmount}
              onChange={(event) =>
                setOpenForm((current) => ({ ...current, openingAmount: event.target.value }))
              }
              inputProps={{ min: 0, step: "any" }}
            />
            <TextField
              label="Notas de apertura"
              multiline
              minRows={3}
              value={openForm.notes}
              onChange={(event) =>
                setOpenForm((current) => ({ ...current, notes: event.target.value }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenTarget(null)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<LockOpenRoundedIcon />}
            onClick={openRegister}
            disabled={saving}
          >
            {saving ? "Abriendo..." : "Abrir caja"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(closeTarget)}
        onClose={saving ? undefined : () => setCloseTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 950 }}>Cerrar {closeTarget?.register?.name}</DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {blindClosing || closeTarget?.totals?.hidden || !canReadExpected ? (
              <Alert severity="info">
                Contá el efectivo físico e ingresá el total. El sistema no muestra el saldo esperado antes del arqueo.
              </Alert>
            ) : (
              <Alert severity="info">
                Saldo esperado:{" "}
                <strong>
                  {formatMoney(
                    closeTarget?.totals?.expected,
                    closeTarget?.register?.currency,
                  )}
                </strong>
              </Alert>
            )}
            <TextField
              type="number"
              label="Saldo contado y declarado"
              required
              autoFocus
              value={closeForm.declaredClosingAmount}
              onChange={(event) =>
                setCloseForm((current) => ({
                  ...current,
                  declaredClosingAmount: event.target.value,
                }))
              }
              inputProps={{ min: 0, step: "any" }}
              helperText="Ingresá exactamente el dinero físico contado."
            />
            <TextField
              label="Notas de cierre"
              multiline
              minRows={3}
              value={closeForm.notes}
              onChange={(event) =>
                setCloseForm((current) => ({ ...current, notes: event.target.value }))
              }
            />
            {!blindClosing && canReadExpected && closeTarget?.totals?.expected !== null ? (
              <Alert
                severity={
                  Number(closeForm.declaredClosingAmount || 0) ===
                  Number(closeTarget?.totals?.expected || 0)
                    ? "success"
                    : "warning"
                }
              >
                Diferencia estimada:{" "}
                <strong>
                  {formatMoney(
                    Number(closeForm.declaredClosingAmount || 0) -
                      Number(closeTarget?.totals?.expected || 0),
                    closeTarget?.register?.currency,
                  )}
                </strong>
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCloseTarget(null)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<LockRoundedIcon />}
            onClick={closeRegister}
            disabled={saving || closeForm.declaredClosingAmount === ""}
          >
            {saving ? "Cerrando..." : "Cerrar y arquear"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(operationTarget)}
        onClose={saving ? undefined : () => setOperationTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 950 }}>
          Operación en {operationTarget?.register?.name}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              label="Movimiento de caja"
              value={operationForm.direction}
              onChange={(event) =>
                setOperationForm((current) => ({
                  ...current,
                  direction: event.target.value,
                  itemId: "",
                  quantity: "1",
                  amount: "",
                  description: "",
                  counterAccountCode:
                    event.target.value === "in" ? "OTHER_INCOME" : "OTHER_EXPENSE",
                }))
              }
            >
              {canCashIn ? (
                <MenuItem value="in">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CallReceivedRoundedIcon color="success" fontSize="small" />
                    <span>Ingreso</span>
                  </Stack>
                </MenuItem>
              ) : null}
              {canCashOut ? (
                <MenuItem value="out">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CallMadeRoundedIcon color="error" fontSize="small" />
                    <span>Egreso</span>
                  </Stack>
                </MenuItem>
              ) : null}
            </TextField>

            {canUseItemSelector ? (
              <Autocomplete
                options={selectableOperationItems}
                value={selectedOperationItem}
                loading={operationItemsLoading}
                getOptionLabel={itemOptionLabel}
                filterOptions={filterOperationItems}
                isOptionEqualToValue={(option, value) => entityId(option) === entityId(value)}
                noOptionsText={
                  operationItemsLoading
                    ? "Cargando ítems..."
                    : operationForm.direction === "in"
                      ? "No hay ítems habilitados para vender"
                      : "No hay ítems habilitados para comprar o registrar como gasto"
                }
                onChange={(_event, item) => {
                  if (!item) {
                    setOperationForm((current) => ({
                      ...current,
                      itemId: "",
                      quantity: "1",
                      amount: "",
                      description: "",
                    }));
                    return;
                  }
                  const accounting = item.accounting || {};
                  const amount = suggestedOperationAmount(item, operationForm.direction);
                  const code = String(item.codigo || item.code || "").trim();
                  const operationName = operationForm.direction === "in"
                    ? "Venta"
                    : itemCapabilities(item).stockTracked
                      ? "Compra"
                      : "Gasto";
                  setOperationForm((current) => ({
                    ...current,
                    itemId: entityId(item),
                    quantity: "1",
                    amount,
                    description: `${operationName} · ${item.nombre || item.name || "Ítem"}`,
                    counterAccountCode:
                      operationForm.direction === "in"
                        ? accounting.salesAccountCode || "SALES"
                        : itemCapabilities(item).stockTracked
                          ? accounting.inventoryAccountCode || "INVENTORY"
                          : accounting.expenseAccountCode ||
                            accounting.purchaseAccountCode ||
                            "OTHER_EXPENSE",
                    reference: current.reference || code,
                  }));
                }}
                renderOption={(props, option) => {
                  const capabilities = itemCapabilities(option);
                  const taxCode = itemTaxCode(option);
                  const taxProfile = (bootstrap.taxProfiles || []).find(
                    (profile) => String(profile.code || "").toUpperCase() === taxCode,
                  );
                  return (
                    <Box component="li" {...props} key={entityId(option)}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 850 }} noWrap>
                          {option.nombre || option.name || "Ítem"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: ERP_BRAND.muted }} noWrap>
                          {ITEM_KIND_LABELS[itemKindOf(option)] || "Ítem"}
                          {option.categoria || option.category
                            ? ` · ${option.categoria || option.category}`
                            : ""}
                          {capabilities.stockTracked
                            ? ` · existencia ${Number(option.stock || 0)}`
                            : ""}
                          {taxProfile
                            ? ` · preparado para facturar con ${taxProfile.name || "impuesto configurado"}`
                            : " · sin configuración fiscal"}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ítem asociado"
                    placeholder="Buscá por nombre, código o categoría"
                    helperText={
                      operationForm.direction === "in"
                        ? "Opcional. Al elegir un ítem se registra una venta cobrada y, si corresponde, se descuenta inventario."
                        : "Opcional. Al elegir un ítem se registra una compra o gasto pagado y, si corresponde, se suma inventario."
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {operationItemsLoading ? (
                            <WebProfitSpinner color="inherit" size={18} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            ) : (
              <Alert severity="info">
                Podés registrar un movimiento manual. Para asociar ítems, tu rol también necesita permiso para
                {operationForm.direction === "in" ? " vender y cobrar" : " comprar o registrar gastos y pagarlos"}.
              </Alert>
            )}

            {operationItemsError ? (
              <Alert severity="warning">
                {operationItemsError} Podés continuar con un movimiento manual.
              </Alert>
            ) : null}

            {selectedOperationItem ? (
              <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 3 }}>
                <Stack spacing={1.2}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>
                        {selectedOperationItem.nombre || selectedOperationItem.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>
                        {operationForm.direction === "in"
                          ? "Se registrará como venta cobrada en esta caja."
                          : selectedCapabilities?.stockTracked
                            ? "Se registrará como compra pagada y aumentará el inventario."
                            : "Se registrará como gasto pagado desde esta caja."}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      color={selectedTaxProfile ? "success" : "warning"}
                      label={selectedTaxProfile ? "Preparado para facturar" : "Falta preparar facturación"}
                      sx={{ fontWeight: 850, alignSelf: "flex-start" }}
                    />
                  </Stack>

                  <Alert severity={selectedTaxProfile ? "success" : "warning"} icon={false}>
                    {selectedTaxProfile ? (
                      <>
                        <strong>Configuración impositiva lista:</strong> {selectedTaxProfile.name || "Impuesto configurado"}
                        {Number(selectedTaxProfile.rate || 0) > 0
                          ? selectedTaxProfile.calculationMode === "fixed"
                            ? ` (${formatMoney(Number(selectedTaxProfile.rate), operationTarget?.register?.currency)} por operación)`
                            : ` (${Number(selectedTaxProfile.rate)}%)`
                          : ""}. La venta o compra guardará este cálculo. La emisión de una factura fiscal depende de que el proyecto tenga un proveedor de facturación conectado.
                      </>
                    ) : (
                      <>
                        <strong>Este ítem no tiene una configuración impositiva.</strong> La operación se guardará sin impuesto. Antes de facturarla fiscalmente, un responsable deberá completar esa configuración y contar con un proveedor de facturación conectado.
                      </>
                    )}
                  </Alert>

                  {!selectedTaxProfile ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        navigate(
                          `/dashboard/proyectos/${projectId}/sistema/items?editar=${encodeURIComponent(
                            entityId(selectedOperationItem),
                          )}`,
                        )
                      }
                      sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 850 }}
                    >
                      Configurar impuestos del ítem
                    </Button>
                  ) : null}

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>
                      <strong>Cuenta:</strong> {selectedAccount?.name || "Cuenta general"}
                    </Typography>
                    {selectedCapabilities?.stockTracked ? (
                      <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>
                        <strong>Existencia:</strong> {Number(selectedOperationItem.stock || 0)} {selectedOperationItem.unidad || selectedOperationItem.unit || "unidades"}
                      </Typography>
                    ) : null}
                    {itemKindOf(selectedOperationItem) === "service" && itemDuration(selectedOperationItem) > 0 ? (
                      <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>
                        <strong>Duración:</strong> {itemDuration(selectedOperationItem)} minutos
                      </Typography>
                    ) : null}
                  </Stack>

                  {canReadItemCosts &&
                  operationForm.direction === "in" &&
                  selectedCapabilities?.stockTracked &&
                  Number(selectedOperationItem.costing?.averageCost || selectedOperationItem.costing?.lastCost || 0) <= 0 ? (
                    <Alert severity="warning" icon={false}>
                      Falta cargar el costo del ítem. La venta descontará inventario, pero el margen y el costo vendido no serán confiables.
                    </Alert>
                  ) : null}
                </Stack>
              </Paper>
            ) : null}

            <Grid container spacing={1.5}>
              {selectedOperationItem ? (
                <Grid item xs={12} sm={4}>
                  <TextField
                    type="number"
                    label="Cantidad"
                    required
                    fullWidth
                    value={operationForm.quantity}
                    onChange={(event) =>
                      setOperationForm((current) => ({ ...current, quantity: event.target.value }))
                    }
                    inputProps={{ min: 0.0001, step: "any" }}
                  />
                </Grid>
              ) : null}
              <Grid item xs={12} sm={selectedOperationItem ? 8 : 12}>
                <TextField
                  type="number"
                  label={selectedOperationItem ? "Importe unitario" : "Importe total"}
                  required
                  fullWidth
                  value={operationForm.amount}
                  onChange={(event) =>
                    setOperationForm((current) => ({ ...current, amount: event.target.value }))
                  }
                  inputProps={{ min: 0, step: "any" }}
                />
              </Grid>
            </Grid>

            {selectedOperationItem && unitAmountValue > 0 ? (
              <Alert severity="info" icon={false}>
                Total estimado: <strong>{formatMoney(estimatedOperationTotal, operationTarget?.register?.currency)}</strong>
                {estimatedTax > 0
                  ? taxIncluded
                    ? ` · incluye ${formatMoney(estimatedTax, operationTarget?.register?.currency)} de impuesto.`
                    : ` · se agregan ${formatMoney(estimatedTax, operationTarget?.register?.currency)} de impuesto.`
                  : "."}
              </Alert>
            ) : null}

            <TextField
              label="Descripción"
              required
              value={operationForm.description}
              onChange={(event) =>
                setOperationForm((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
            />

            {!selectedOperationItem ? (
              <TextField
                select
                label="Categoría contable"
                value={operationForm.counterAccountCode}
                onChange={(event) =>
                  setOperationForm((current) => ({
                    ...current,
                    counterAccountCode: event.target.value,
                  }))
                }
              >
                {operationAccounts.map((account) => (
                  <MenuItem key={account.code} value={account.code}>
                    {account.name} · {ACCOUNT_TYPE_LABELS[account.type]}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}

            <TextField
              label="Número o referencia"
              value={operationForm.reference}
              onChange={(event) =>
                setOperationForm((current) => ({
                  ...current,
                  reference: event.target.value,
                }))
              }
            />
            <TextField
              label="Notas internas"
              multiline
              minRows={2}
              value={operationForm.notes}
              onChange={(event) =>
                setOperationForm((current) => ({ ...current, notes: event.target.value }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOperationTarget(null)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color={operationForm.direction === "in" ? "success" : "error"}
            onClick={recordOperation}
            disabled={saving}
          >
            {saving ? "Registrando..." : "Registrar operación"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(reviewTarget)}
        onClose={saving ? undefined : () => setReviewTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 950 }}>
          Revisar cierre {reviewTarget?.sessionNumber}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity={Number(reviewTarget?.difference || 0) === 0 ? "success" : "warning"}>
              Diferencia registrada:{" "}
              <strong>{formatMoney(reviewTarget?.difference, reviewTarget?.currency)}</strong>
            </Alert>
            <TextField
              select
              label="Resultado de revisión"
              value={reviewForm.reviewStatus}
              onChange={(event) =>
                setReviewForm((current) => ({
                  ...current,
                  reviewStatus: event.target.value,
                }))
              }
            >
              <MenuItem value="approved">Aprobar cierre</MenuItem>
              <MenuItem value="observed">Marcar como observado</MenuItem>
            </TextField>
            <TextField
              label="Notas de revisión"
              multiline
              minRows={3}
              value={reviewForm.reviewNotes}
              onChange={(event) =>
                setReviewForm((current) => ({
                  ...current,
                  reviewNotes: event.target.value,
                }))
              }
            />
            {Number(reviewTarget?.difference || 0) !== 0 ? (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={reviewForm.createAdjustment}
                      onChange={(event) =>
                        setReviewForm((current) => ({
                          ...current,
                          createAdjustment: event.target.checked,
                        }))
                      }
                    />
                  }
                  label="Crear ajuste contable por la diferencia"
                />
                {reviewForm.createAdjustment ? (
                  <TextField
                    label="Motivo del ajuste"
                    multiline
                    minRows={2}
                    value={reviewForm.adjustmentNotes}
                    onChange={(event) =>
                      setReviewForm((current) => ({
                        ...current,
                        adjustmentNotes: event.target.value,
                      }))
                    }
                  />
                ) : null}
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setReviewTarget(null)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <WebProfitSpinner size={16} /> : <FactCheckRoundedIcon />}
            onClick={reviewSession}
            disabled={saving}
          >
            {saving ? "Guardando..." : "Confirmar revisión"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
